import { useParams } from 'react-router-dom';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { TextField, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import { NotificationImportant } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { z } from 'zod';
import { stepValidator } from '@/components/wizard/validateWithZod';

const STEPS = [
  { id: 'details', title: 'Alert Details' },
  { id: 'action', title: 'Take Action' },
];

const ALERT_TYPES = ['ProbationExpiry', 'FixedTermExpiry'];
const ACTIONS = ['Initiate Review', 'Initiate Renewal', 'Acknowledge', 'No Action'];

interface AlertDetails {
  alertType: string;
  employeeName: string;
  employeeId: string;
  contractEndDate: string;
  actionRequired: string;
}

interface FormValues {
  alertType: string;
  employeeName: string;
  employeeId: string;
  contractEndDate: string;
  actionRequired: string;
  action: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  alertType: '',
  employeeName: '',
  employeeId: '',
  contractEndDate: '',
  actionRequired: '',
  action: '',
  notes: '',
};

const MOCK_ALERTS: Record<string, AlertDetails> = {
  'alert-001': {
    alertType: 'ProbationExpiry',
    employeeName: 'Ntsako Mthombeni',
    employeeId: 'EMP-2024-0892',
    contractEndDate: '2026-06-15',
    actionRequired: 'Schedule probation review meeting before contract end date',
  },
  'alert-002': {
    alertType: 'FixedTermExpiry',
    employeeName: 'Sophia van der Berg',
    employeeId: 'EMP-2024-0456',
    contractEndDate: '2026-05-31',
    actionRequired: 'Decide on contract renewal or termination',
  },
};

const stepSchemas = {
  action: z.object({
    action: z.string().min(1, 'Action is required'),
  }),
};

const validateStep = (stepId: string, values: FormValues) => {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };
  return stepValidator(schema)(values as unknown as Record<string, unknown>);
};

export const AlertAcknowledgement = () => {
  const { alertId } = useParams<{ alertId: string }>();
  const dataProvider = useDataProvider();

  const initialValues = {
    ...INITIAL_VALUES,
    ...(alertId && MOCK_ALERTS[alertId] ? MOCK_ALERTS[alertId] : {}),
  };

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepFn,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues,
    dataProvider,
    resource: 'contract-alerts',
  });

  const vals = values as unknown as FormValues;

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const getActionFeedbackMessage = (action: string): string => {
    switch (action) {
      case 'Initiate Review':
        return 'Probation review meeting will be scheduled';
      case 'Initiate Renewal':
        return 'Contract renewal process will be initiated';
      case 'Acknowledge':
        return 'Alert has been acknowledged';
      case 'No Action':
        return 'Alert acknowledged with no action required';
      default:
        return 'Action recorded';
    }
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, vals);
    if (!result.isValid) {
      return;
    }
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleBack = () => {
    prevStep();
  };

  const handleComplete = async () => {
    await complete();
  };

  const handleReset = () => {
    reset();
  };

  const renderCompletedContent = () => (
    <OutcomeSummary
      title="Alert Acknowledged"
      processId={createdId || undefined}
      fields={[
        { label: 'Alert Type', value: vals.alertType === 'ProbationExpiry' ? 'Probation Expiry' : 'Fixed Term Expiry' },
        { label: 'Employee', value: vals.employeeName },
        { label: 'Employee #', value: vals.employeeId },
        { label: 'Contract End Date', value: vals.contractEndDate },
        { label: 'Action Taken', value: vals.action },
        { label: 'Notes', value: vals.notes || 'None' },
      ]}
      footerActions={[
        { label: 'View Alerts', to: '/contract-alerts' },
        { label: 'Acknowledge Another', onClick: handleReset },
      ]}
    />
  );

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onNext={handleNext}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      {isCompleted ? (
        renderCompletedContent()
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <NotificationImportant color="warning" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700}>
              Contract Alert Acknowledgement
            </Typography>
          </Box>

          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Step {currentStepIndex + 1} of {totalSteps}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              {currentStep.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {getStepDescription(currentStep.id)}
            </Typography>

            {renderFormFields(currentStep.id, vals, errors, updateField)}
          </Paper>
        </>
      )}
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    details: 'Review the contract alert details',
    action: 'Select and record the action taken for this alert',
  };
  return descriptions[stepId] || '';
}

function renderFormFields(stepId: string, vals: FormValues, errors: Record<string, string>, updateField: (field: keyof FormValues, value: unknown) => void): React.ReactNode {
    switch (stepId) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {vals.alertType === 'ProbationExpiry'
                  ? 'Probation period ending soon - schedule review meeting'
                  : 'Fixed-term contract expiring - decide on renewal or termination'}
              </Alert>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Alert Type</InputLabel>
                <Select value={vals.alertType} label="Alert Type" disabled>
                  <MenuItem value="ProbationExpiry">Probation Expiry</MenuItem>
                  <MenuItem value="FixedTermExpiry">Fixed Term Expiry</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contract End Date"
                type="date"
                value={vals.contractEndDate}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Employee Name"
                value={vals.employeeName}
                fullWidth
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Employee #"
                value={vals.employeeId}
                fullWidth
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Action Required"
                value={vals.actionRequired}
                fullWidth
                multiline
                rows={2}
                disabled
                helperText="What needs to be done before contract end date"
              />
            </Grid>
          </Grid>
        );

      case 'action':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.action} required>
                <InputLabel>Action</InputLabel>
                <Select
                  value={vals.action}
                  label="Action"
                  onChange={e => updateField('action', e.target.value)}
                >
                  {ACTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
                {errors.action && <Typography variant="caption" color="error">{errors.action}</Typography>}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                value={vals.notes}
                onChange={e => updateField('notes', e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="Add any additional notes or context for this acknowledgement..."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                BCEA Section 38 requires written confirmation of employment terms.
                Probation reviews must occur before expiry per LRA Schedule 8.
              </Alert>
            </Grid>
          </Grid>
        );

      default:
      return null;
  }
}